import { useState, useMemo, useCallback, useEffect } from 'react';
import {
  Box,
  Button,
  Chip,
  Card,
  Typography,
  TextField,
  MenuItem,
  Stack,
} from '@mui/material';
import { DataGrid, GridColDef, GridSortModel } from '@mui/x-data-grid';
import { Icon } from '@iconify/react';
import intl from 'components/i18n/ReactIntlWrapper';
import { useToastMessageQueue } from 'components/ToastMessages/ToastMessageProvider';
import { PermissionsGate } from 'components/authorization/PermissionsGate';
import { ENTITY_TYPE } from 'common/constants';
import DefaultActions from 'components/DataGrid/DefaultActions';
import { useApi } from 'api/ApiProvider';
import { useDialog } from 'common/DialogProvider';
import type { TaxRate } from 'axiosApi/models';
import TaxFormDrawer from './TaxFormDrawer';
import { usePaginationPreference } from 'hooks/usePaginationPreference';

const AppliesTo = {
  ALL: 0,
  ITEM: 1,
  INVOICE: 2,
} as const;

const TaxList = () => {
  const api = useApi();
  const toast = useToastMessageQueue();
  const { openDialog, closeDialog } = useDialog();

  const [paginationModel, setPaginationModel] = usePaginationPreference('taxManagement', 25);
  const [sortModel, setSortModel] = useState<GridSortModel>([
    { field: 'country', sort: 'asc' },
  ]);
  const [selectedTaxRate, setSelectedTaxRate] = useState<TaxRate | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Data states
  const [data, setData] = useState<any>(null);
  const [countriesData, setCountriesData] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<any>(null);

  // Filter states
  const [nameFilter, setNameFilter] = useState('');
  const [countryFilter, setCountryFilter] = useState('');
  const [appliesToFilter, setAppliesToFilter] = useState<string>('all');

  // Fetch countries for filter
  useEffect(() => {
    const fetchCountries = async () => {
      if (!api) return;
      try {
        const response = await api.addressApi.apiVversionListCountriesGet('1');
        setCountriesData(response.data);
      } catch (err) {
        console.error('Error fetching countries:', err);
      }
    };
    fetchCountries();
  }, [api]);

  const countries = useMemo(() => {
    return countriesData?.data?.queryResult || [];
  }, [countriesData]);

  // Fetch tax rates with pagination, sorting, and filtering
  const fetchTaxRates = useCallback(async () => {
    if (!api) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const sortField = sortModel[0]?.field || 'country';
      const sortOrder = sortModel[0]?.sort || 'asc';

      // Prepare filter values (ensure undefined if empty/default)
      const nameParam = nameFilter?.trim() || undefined;
      const countryParam = countryFilter ? parseInt(countryFilter) : undefined;
      const appliesToParam = appliesToFilter !== 'all' ? parseInt(appliesToFilter) : undefined;

      const currentPage = paginationModel.page;
      const currentPageSize = paginationModel.pageSize;

      console.log('🔍 Fetching tax rates with filters:', {
        dataGridPage: currentPage,
        backendPage: currentPage + 1,
        pageSize: currentPageSize,
        sort: sortField,
        order: sortOrder,
        name: nameParam,
        countryId: countryParam,
        appliesTo: appliesToParam
      });

      const response = await api.invoiceApi.apiVversionTaxRateGet(
        '1',
        currentPage + 1, // Convert to 1-based indexing for backend
        currentPageSize,
        sortField,
        sortOrder,
        nameParam,
        countryParam,
        appliesToParam
      );
      
      setData((response.data as any).data);
    } catch (err: any) {
      console.error('❌ API Error:', err);
      // Handle 404 as empty result set, not an error
      if (err?.response?.status === 404) {
        setData({ queryResult: [], totalRecords: 0 });
      } else {
        setError(err);
      }
    } finally {
      setLoading(false);
    }
  }, [api, paginationModel.page, paginationModel.pageSize, sortModel, nameFilter, countryFilter, appliesToFilter]);

  // Fetch data when dependencies change
  useEffect(() => {
    fetchTaxRates();
  }, [fetchTaxRates]);

  // Reset pagination to page 0 when filters change
  useEffect(() => {
    setPaginationModel((current) => ({ ...current, page: 0 }));
  }, [nameFilter, countryFilter, appliesToFilter]);

  // Create skeleton rows for loading state
  const skeletonRows = useMemo(() => {
    return Array.from({ length: paginationModel.pageSize }, (_, index) => ({
      id: `skeleton-${index}`,
    }));
  }, [paginationModel.pageSize]);

  const handleClearFilters = useCallback(() => {
    setNameFilter('');
    setCountryFilter('');
    setAppliesToFilter('all');
  }, []);

  const handleCreate = useCallback(() => {
    setSelectedTaxRate(null);
    setDrawerOpen(true);
  }, []);

  const handleEdit = useCallback((taxRate: TaxRate) => {
    setSelectedTaxRate(taxRate);
    setDrawerOpen(true);
  }, []);

  const handleDelete = useCallback(
    (id: number, taxName: string) => {
      const dialogId = openDialog({
        title: intl.get('taxManagement.deleteTitle'),
        message: intl.get('taxManagement.confirmDelete', { name: taxName }),
        type: 'confirm',
        confirmButton: {
          confirmLabel: intl.get('taxManagement.delete'),
          handleConfirm: async () => {
            try {
              await api.invoiceApi.apiVversionTaxRateIdDelete(id, '1');
              toast.success({ body: intl.get('taxManagement.deleteSuccess') });
              fetchTaxRates(); // Refetch data after delete
              closeDialog(dialogId);
            } catch (err) {
              toast.error({ body: intl.get('taxManagement.deleteError') });
              closeDialog(dialogId);
            }
          },
        },
        cancelButton: {
          cancelLabel: intl.get('taxManagement.cancel'),
          handleCancel: () => {
            closeDialog(dialogId);
          },
        },
      });
    },
    [api, toast, openDialog, closeDialog, fetchTaxRates]
  );

  const handleDrawerClose = useCallback(() => {
    setDrawerOpen(false);
    setSelectedTaxRate(null);
  }, []);

  const handleSuccess = useCallback(() => {
    fetchTaxRates(); // Refetch data after success
    handleDrawerClose();
  }, [fetchTaxRates, handleDrawerClose]);

  const columns = useMemo<GridColDef<TaxRate>[]>(
    () => [
      {
        field: 'country',
        headerName: intl.get('taxManagement.country'),
        flex: 1,
        minWidth: 150,
        valueGetter: (value, row) => row.country?.name || '',
      },
      {
        field: 'name',
        headerName: intl.get('taxManagement.name'),
        flex: 1,
        minWidth: 150,
      },
      {
        field: 'rate',
        headerName: intl.get('taxManagement.rate'),
        width: 100,
        valueFormatter: (value) => {
          if (value == null) return '';
          return `${value}%`;
        },
      },
      {
        field: 'amount',
        headerName: intl.get('taxManagement.amount'),
        width: 120,
        valueFormatter: (value: number | null) => {
          if (value == null) return '';
          return `$${value.toFixed(2)}`;
        },
      },
      {
        field: 'appliesTo',
        headerName: intl.get('taxManagement.appliesTo'),
        width: 150,
        renderCell: (params) => {
          const value = params.value;
          let label = '';
          let color: 'default' | 'primary' | 'secondary' | 'success' = 'default';

          switch (value) {
            case AppliesTo.ALL:
              label = intl.get('taxManagement.appliesToAll');
              color = 'success';
              break;
            case AppliesTo.ITEM:
              label = intl.get('taxManagement.appliesToItem');
              color = 'primary';
              break;
            case AppliesTo.INVOICE:
              label = intl.get('taxManagement.appliesToInvoice');
              color = 'secondary';
              break;
            default:
              label = intl.get('taxManagement.appliesToUnknown');
          }

          return <Chip label={label} color={color} size="small" />;
        },
      },
      {
        field: 'actions',
        headerName: intl.get('taxManagement.actions'),
        width: 120,
        sortable: false,
        renderCell: (params) => (
          <DefaultActions
            row={params.row}
            entityType={ENTITY_TYPE.EMPLOYEE}
            onEdit={handleEdit}
            onDelete={(row) => handleDelete(row.id!, row.name || '')}
            identifier="ui.taxmanagement.list.row-actions"
          />
        ),
      },
    ],
    [handleEdit, handleDelete]
  );

  if (error) {
    return (
      <Card sx={{ m: 2 }}>
        <Box sx={{ p: 3 }}>
          <Chip label={intl.get('taxManagement.loadError')} color="error" />
        </Box>
      </Card>
    );
  }

  return (
    <Card sx={{ m: 2 }}>
      <Box sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography color="primary" variant="h2" component="h2">
            {intl.get('taxManagement.title')}
          </Typography>
          <PermissionsGate
            identifier="ui.taxmanagement.list.create-button"
            viewRoles={[]}
            editRoles={[]}
          >
            <Button
              variant="contained"
              startIcon={<Icon icon="mdi:plus" />}
              onClick={handleCreate}
            >
              {intl.get('taxManagement.create')}
            </Button>
          </PermissionsGate>
        </Box>

        {/* Filters */}
        <Card sx={{ mb: 3, p: 2, bgcolor: 'background.neutral' }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              fullWidth
              size="small"
              label={intl.get('taxManagement.filterByName')}
              placeholder={intl.get('taxManagement.searchPlaceholder')}
              value={nameFilter}
              onChange={(e) => setNameFilter(e.target.value)}
              slotProps={{
                input: {
                  startAdornment: <Icon icon="mdi:magnify" style={{ marginRight: 8, color: '#666' }} />,
                },
              }}
            />
            <TextField
              fullWidth
              select
              size="small"
              label={intl.get('taxManagement.filterByCountry')}
              value={countryFilter}
              onChange={(e) => setCountryFilter(e.target.value)}
            >
              <MenuItem value="">{intl.get('taxManagement.allCountries')}</MenuItem>
              {countries.map((country: any) => (
                <MenuItem key={country.id} value={country.id}>
                  {intl.get(country.label)}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              fullWidth
              select
              size="small"
              label={intl.get('taxManagement.filterByAppliesTo')}
              value={appliesToFilter}
              onChange={(e) => setAppliesToFilter(e.target.value)}
            >
              <MenuItem value="all">{intl.get('taxManagement.allTypes')}</MenuItem>
              <MenuItem value={AppliesTo.ALL}>{intl.get('taxManagement.appliesToAll')}</MenuItem>
              <MenuItem value={AppliesTo.ITEM}>{intl.get('taxManagement.appliesToItem')}</MenuItem>
              <MenuItem value={AppliesTo.INVOICE}>{intl.get('taxManagement.appliesToInvoice')}</MenuItem>
            </TextField>
            <Button
              variant="outlined"
              onClick={handleClearFilters}
              startIcon={<Icon icon="mdi:filter-off" />}
              sx={{ minWidth: 160 }}
            >
              {intl.get('taxManagement.clearFilters')}
            </Button>
          </Stack>
        </Card>

        <Box sx={{ width: '100%', display: 'flex' }}>
          <DataGrid
            rows={loading ? skeletonRows : (data?.queryResult || [])}
            columns={columns}
            getRowId={(row) => row.id || 0}
            rowCount={data?.totalRecords || 0}
            loading={loading}
            pageSizeOptions={[10, 25, 50, 100]}
            paginationModel={paginationModel}
            paginationMode="server"
            onPaginationModelChange={setPaginationModel}
            sortingMode="server"
            sortModel={sortModel}
            onSortModelChange={setSortModel}
            disableRowSelectionOnClick
            autoHeight
            sx={{
              '& .MuiDataGrid-row:hover': {
                cursor: 'pointer',
              },
            }}
          />
        </Box>

        <TaxFormDrawer
          open={drawerOpen}
          taxRate={selectedTaxRate}
          onClose={handleDrawerClose}
          onSuccess={handleSuccess}
        />
      </Box>
    </Card>
  );
};

export default TaxList;
